import React, { createContext, FC, ReactNode, useState } from 'react';

import { useSessionContext } from '~/contexts/SessionContext';

import { usePrevious } from '../hooks/usePrevious';
import { mainSidebar, SideBarContent, SideBarSection } from '../views/layouts/SideBar';

export interface SidebarContextProps {
  sidebarContent: SideBarContent;
  sidebarContentOld: SideBarContent;
  setSidebarContent: (content: SideBarContent) => void;
}

interface SidebarProviderProps {
  children: ReactNode;
}

const SidebarContext = createContext<SidebarContextProps>({
  sidebarContent: undefined,
  sidebarContentOld: undefined,
  setSidebarContent: undefined,
});

const isDetailsPagePath = (pathname: string) =>
  /^\/app\/(organizations|audiences)\/[^/]+(?:\/(?!create-segment)|$)/.test(pathname);

export const SidebarProvider: FC<SidebarProviderProps> = ({ children }) => {
  const { orgMenuItemTypes, hasAudienceOnlyAccess, hasInviteToAnOrganization, hasAccessToAudience, loading } =
    useSessionContext();
  const [sidebarContent, setContentLocal] = useState<SideBarContent>(() =>
    isDetailsPagePath(window.location.pathname)
      ? { name: 'detail', sections: [{ items: [] }] }
      : mainSidebar(orgMenuItemTypes, hasAudienceOnlyAccess, hasInviteToAnOrganization, hasAccessToAudience, loading),
  );
  const sidebarContentOld = usePrevious(sidebarContent);
  const isEqualSections = (sections1: SideBarSection[], sections2: SideBarSection[]) => {
    const stringifySections = (sectionsArray: SideBarSection[]) =>
      JSON.stringify(sectionsArray, (key, value) => (key !== 'icon' ? value : undefined));
    return stringifySections(sections1) === stringifySections(sections2);
  };

  const setContent = (contentNew: SideBarContent) => {
    if (!isEqualSections(sidebarContent?.sections, contentNew?.sections)) {
      setContentLocal(contentNew);
    }
  };

  return (
    <SidebarContext.Provider
      value={{
        sidebarContent,
        sidebarContentOld,
        setSidebarContent: setContent,
      }}
    >
      {children}
    </SidebarContext.Provider>
  );
};

export const useSidebarContext: () => SidebarContextProps = () => {
  return React.useContext(SidebarContext);
};
